import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import IOSToggle from '@/components/IOSToggle';
import OnboardingFeaturesWizard from '@/components/OnboardingFeaturesWizard';
import { FEATURES, FEATURE_CATEGORIES, ROL_OPTIES, mergeFeatures, extractRootMirrorFields } from '@/lib/features';
import { invalidateFeatureCache } from '@/hooks/useFeature';
import { Info, Check, LockKey, Rocket } from '@phosphor-icons/react';

/**
 * FunctiesPanel — Instellingen-tab waar gebruiker per feature een toggle
 * en een minimum-rol kan instellen.
 */
export default function FunctiesPanel() {
  const [features, setFeatures] = useState(() => mergeFeatures({}));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [filter, setFilter] = useState('');
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    API.get('/instellingen').then(r => {
      // Merge features + seed legacy top-level velden (bv. webshop_actief) via syncs_to_root
      setFeatures(mergeFeatures(r.data?.features || {}, r.data || {}));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Bridge: mirror bv. features.webshop_actief.actief → top-level instellingen.webshop_actief
      const rootMirror = extractRootMirrorFields(features);
      await API.put('/instellingen', { features, ...rootMirror });
      invalidateFeatureCache(); // Sidebar/UI moet direct reageren op toggle-wijzigingen
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const updateFeature = (key, patch) => {
    setFeatures(p => ({ ...p, [key]: { ...p[key], ...patch } }));
  };

  const grouped = useMemo(() => {
    const out = {};
    FEATURES.forEach(f => {
      if (filter && !(`${f.label} ${f.beschrijving}`.toLowerCase().includes(filter.toLowerCase()))) return;
      const cat = f.categorie;
      out[cat] = out[cat] || [];
      out[cat].push(f);
    });
    return out;
  }, [filter]);

  const totaalActief = Object.values(features).filter(f => f.actief).length;
  const totaalFeatures = FEATURES.length;

  return (
    <div className="space-y-5" data-testid="functies-panel">
      {/* Kop */}
      <div className="section-card">
        <div className="section-body space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center font-bold">⚙</div>
            <div className="flex-1">
              <div className="text-sm font-bold text-gray-900">Functie-overzicht</div>
              <p className="text-[12px] text-gray-600 mt-1">
                Hier staat een lijst van alle nieuwe functies die aan uw facturatie-app toegevoegd zijn.
                Schakel elke functie afzonderlijk aan of uit en kies welke gebruikersrol toegang heeft.
                Dit helpt u een overzicht te houden van wat de app kan en voor wie.
              </p>
              <div className="flex items-center gap-4 mt-3 text-[11px]">
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <strong>{totaalActief}/{totaalFeatures}</strong> functies actief
                </span>
                <span className="text-gray-400">·</span>
                <span className="inline-flex items-center gap-1">
                  <LockKey size={12} /> Rol: <strong>admin</strong> &gt; <strong>user</strong> &gt; <strong>alleen-lezen</strong>
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Zoek een functie…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="field-input text-sm flex-1"
              data-testid="functies-filter"
            />
            <button
              onClick={() => setShowWizard(true)}
              className="btn btn-ghost btn-sm inline-flex items-center gap-1.5 border border-[#002FA7]/30 text-[#002FA7] hover:bg-[#F0F4FF] whitespace-nowrap"
              data-testid="functies-wizard-btn"
              title="Open de onboarding-wizard opnieuw"
            >
              <Rocket size={14} weight="duotone" /> Herstart wizard
            </button>
          </div>
        </div>
      </div>

      {/* Feature-kaarten per categorie */}
      {Object.entries(grouped).map(([catKey, catFeatures]) => {
        const cat = FEATURE_CATEGORIES[catKey] || { label: catKey, kleur: '#6b7280' };
        return (
          <div key={catKey} className="section-card">
            <div className="section-header flex items-center gap-2">
              <div className="w-1 h-4" style={{ background: cat.kleur }}></div>
              <span className="section-title">{cat.label}</span>
              <span className="text-[11px] text-gray-400 ml-2">({catFeatures.length})</span>
            </div>
            <div className="section-body">
              <div className="divide-y divide-gray-100">
                {catFeatures.map(f => {
                  const state = features[f.key] || { actief: f.default_actief, min_rol: f.default_min_rol };
                  return (
                    <div key={f.key} className={`py-4 flex items-start gap-4 transition-colors ${!state.actief ? 'opacity-60' : ''}`} data-testid={`feature-${f.key}`}>
                      {/* Toggle */}
                      <div className="flex-shrink-0 pt-0.5">
                        <IOSToggle
                          checked={state.actief}
                          onChange={v => updateFeature(f.key, { actief: v })}
                          size="md"
                          testid={`feature-toggle-${f.key}`}
                        />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-gray-900">{f.label}</div>
                          {state.actief && <span className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5">AAN</span>}
                          {!state.actief && <span className="text-[10px] bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5">UIT</span>}
                        </div>
                        <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{f.beschrijving}</p>
                        {f.voordelen && (
                          <p className="text-[11px] text-gray-500 italic mt-1">
                            <Check size={11} className="inline text-green-600 mr-1" weight="bold" />
                            {f.voordelen}
                          </p>
                        )}
                      </div>
                      {/* Rol-selectie */}
                      <div className="flex-shrink-0" style={{ width: 200 }}>
                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
                          <LockKey size={10} /> Toegang
                        </label>
                        <select
                          value={state.min_rol}
                          onChange={e => updateFeature(f.key, { min_rol: e.target.value })}
                          className="field-input text-xs"
                          disabled={!state.actief}
                          data-testid={`feature-rol-${f.key}`}
                        >
                          {ROL_OPTIES.map(r => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                          ))}
                        </select>
                        <div className="text-[10px] text-gray-400 mt-1">
                          {ROL_OPTIES.find(r => r.key === state.min_rol)?.beschrijving}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {/* Save-actie */}
      <div className="section-card">
        <div className="section-body flex items-center justify-between">
          <div className="text-[11px] text-gray-500 flex items-start gap-2">
            <Info size={14} className="text-[#002FA7] flex-shrink-0 mt-0.5" weight="duotone" />
            <span>Wijzigingen zijn direct actief na opslaan. Gebruikers moeten de pagina herladen om uitgeschakelde knoppen te zien verdwijnen.</span>
          </div>
          <div className="flex items-center gap-2">
            {saved && <span className="text-[12px] text-green-700 font-semibold inline-flex items-center gap-1"><Check size={14} weight="bold" /> Opgeslagen</span>}
            <button onClick={save} disabled={saving} className="btn btn-primary btn-sm" data-testid="functies-opslaan">
              {saving ? 'Opslaan…' : 'Instellingen opslaan'}
            </button>
          </div>
        </div>
      </div>

      {showWizard && <OnboardingFeaturesWizard onClose={() => setShowWizard(false)} onComplete={() => setShowWizard(false)} />}
    </div>
  );
}
